import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UserManagementService } from '../../services/user-management.service';
import { ToastService } from '../../../shared/services/toast.service';
import {
  CreateManagementUserRequest,
  DmeOrganization,
  ServiceCenter,
} from '../../../shared/models/user-management.model';
import { UserRole, UserRoleIdMap } from '../../../shared/models/user.model';
import { WebMcpConfirmService } from '../webmcp-confirm.service';
import { WebMcpClient, WebMcpTool, WebMcpToolResult } from '../webmcp.types';
import {
  COUNTRY_CODE_PATTERN,
  EMAIL_PATTERN,
  PHONE_PATTERN,
  asTrimmedString,
  describeOptions,
  errorResult,
  extractErrorMessage,
  findMissingFields,
  matchOption,
  textResult,
} from '../webmcp-tool-helpers';

export const CREATE_USER_TOOL_NAME = 'create_user';

export interface CreateUserToolInput {
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  countryCode: string;
  phone: string;
  role: string;
  organization?: string;
  serviceCenter?: string;
}

// Role labels as shown in the Create User dialog, mapped to backend role ids.
const ROLE_ID_BY_LABEL: Record<string, number> = {
  'Super Admin': UserRoleIdMap[UserRole.Super_Admin],
  'Caire Admin': UserRoleIdMap[UserRole.App_Admin],
  'DME Admin': UserRoleIdMap[UserRole.DME_Admin],
  'DME User': UserRoleIdMap[UserRole.DME_User],
};

/**
 * Roles the signed-in user may create. Mirrors CreateUserDialogComponent, except DME Users
 * get nothing because routeAccessGuard keeps them out of User Management.
 */
export function getCreatableRoles(roleId?: number): string[] {
  if (roleId === UserRoleIdMap[UserRole.Super_Admin]) {
    return ['Super Admin', 'Caire Admin', 'DME Admin', 'DME User'];
  }
  if (roleId === UserRoleIdMap[UserRole.App_Admin]) {
    return ['Caire Admin', 'DME Admin', 'DME User'];
  }
  if (roleId === UserRoleIdMap[UserRole.DME_Admin]) {
    return ['DME Admin', 'DME User'];
  }
  return [];
}

/** Builds the "create_user" WebMCP tool, which follows the same flow as the Create User dialog. */
@Injectable({
  providedIn: 'root',
})
export class CreateUserTool {
  private userManagementService = inject(UserManagementService);
  private confirmService = inject(WebMcpConfirmService);
  private toastService = inject(ToastService);

  /** Returns null when the signed-in user is not allowed to create users. */
  build(currentUser: any): WebMcpTool<CreateUserToolInput> | null {
    const allowedRoles = getCreatableRoles(Number(currentUser?.roleId));
    if (!allowedRoles.length) {
      return null;
    }

    return {
      name: CREATE_USER_TOOL_NAME,
      title: 'Create user',
      description:
        'Create a myCAIRE portal user (not a patient). The user receives an invitation email to set their password. ' +
        'DME Admin and DME User roles belong to a DME organization; DME User also needs a service center. ' +
        'Organization and service center may be given by name or code. If one is missing or unknown, ' +
        'the error lists the valid options. The signed-in person must approve the request before the user is created.',
      inputSchema: {
        type: 'object',
        properties: {
          firstName: { type: 'string', description: 'First name.' },
          middleName: { type: 'string', description: 'Middle name (optional).' },
          lastName: { type: 'string', description: 'Last name.' },
          email: { type: 'string', format: 'email', description: 'Email address; the invitation is sent here.' },
          countryCode: { type: 'string', pattern: COUNTRY_CODE_PATTERN.source, description: 'Phone country code, e.g. "+1".' },
          phone: { type: 'string', pattern: PHONE_PATTERN.source, description: '10-digit phone number, digits only.' },
          role: { type: 'string', enum: allowedRoles, description: 'Role to assign.' },
          organization: {
            type: 'string',
            description: 'DME organization name or code. Required for DME Admin and DME User unless only one organization is available.',
          },
          serviceCenter: { type: 'string', description: 'Service center name or code. Required for DME User.' },
        },
        required: ['firstName', 'lastName', 'email', 'countryCode', 'phone', 'role'],
      },
      execute: (input, client) => this.execute(input, currentUser, allowedRoles, client),
    };
  }

  private async execute(
    input: CreateUserToolInput,
    currentUser: any,
    allowedRoles: string[],
    client?: WebMcpClient,
  ): Promise<WebMcpToolResult> {
    const missing = findMissingFields(input as any, ['firstName', 'lastName', 'email', 'countryCode', 'phone', 'role']);
    if (missing.length) {
      return errorResult(`Missing required fields: ${missing.join(', ')}.`);
    }

    const email = asTrimmedString(input.email);
    const countryCode = asTrimmedString(input.countryCode);
    const phone = asTrimmedString(input.phone);
    const role = allowedRoles.find((allowed) => allowed.toLowerCase() === asTrimmedString(input.role).toLowerCase());

    if (!EMAIL_PATTERN.test(email)) {
      return errorResult('email is not a valid email address.');
    }
    if (!COUNTRY_CODE_PATTERN.test(countryCode)) {
      return errorResult('countryCode must be "+" followed by 1-4 digits, e.g. "+1".');
    }
    if (!PHONE_PATTERN.test(phone)) {
      return errorResult('phone must be exactly 10 digits.');
    }
    if (!role) {
      return errorResult(`You are not allowed to create role "${asTrimmedString(input.role)}". Allowed roles: ${allowedRoles.join(', ')}.`);
    }

    let organization: DmeOrganization | undefined;
    let serviceCenter: ServiceCenter | undefined;
    const needsOrganization = role === 'DME Admin' || role === 'DME User';

    try {
      if (needsOrganization) {
        const organizations = await firstValueFrom(this.userManagementService.getAllDmeOrgs());
        const requested = asTrimmedString(input.organization);
        organization = requested
          ? matchOption(organizations, requested, (o) => o.dmeOrgCode, (o) => o.name)
          : organizations.length === 1 ? organizations[0] : undefined;

        if (!organization) {
          const reason = requested ? `Organization "${requested}" was not found.` : 'organization is required for this role.';
          return errorResult(`${reason} Available organizations: ${describeOptions(organizations, (o) => o.dmeOrgCode, (o) => o.name)}.`);
        }
      }

      if (role === 'DME User' && organization) {
        const serviceCenters = await firstValueFrom(this.userManagementService.getAllServiceCenters(organization.dmeOrgCode));
        const requested = asTrimmedString(input.serviceCenter);
        serviceCenter = requested
          ? matchOption(serviceCenters, requested, (s) => s.scCode, (s) => s.name)
          : serviceCenters.length === 1 ? serviceCenters[0] : undefined;

        if (!serviceCenter) {
          const reason = requested ? `Service center "${requested}" was not found.` : 'serviceCenter is required for DME User.';
          return errorResult(`${reason} Available service centers in ${organization.name}: ${describeOptions(serviceCenters, (s) => s.scCode, (s) => s.name)}.`);
        }
      }
    } catch (error) {
      return errorResult(`Could not load organizations or service centers: ${extractErrorMessage(error)}`);
    }

    const payload: CreateManagementUserRequest = {
      loggedInUserCode: asTrimmedString(currentUser?.userCode),
      dmeOrgCode: asTrimmedString(organization?.dmeOrgCode),
      serviceCenterCode: asTrimmedString(serviceCenter?.scCode),
      mrid: '',
      email,
      roleId: ROLE_ID_BY_LABEL[role],
      firstName: asTrimmedString(input.firstName),
      middleName: asTrimmedString(input.middleName),
      lastName: asTrimmedString(input.lastName),
      countryCode,
      phone,
    };

    const fullName = [payload.firstName, payload.middleName, payload.lastName].filter(Boolean).join(' ');
    const approved = await this.confirmService.confirm(
      {
        title: 'Approve new user?',
        message: 'An AI assistant wants to create this user and send them an invitation email.',
        details: [
          { label: 'Name', value: fullName },
          { label: 'Email', value: email },
          { label: 'Phone', value: `${countryCode} ${phone}` },
          { label: 'Role', value: role },
          { label: 'Organization', value: organization?.name ?? '' },
          { label: 'Service center', value: serviceCenter?.name ?? '' },
        ],
      },
      client,
    );

    if (!approved) {
      return errorResult('The signed-in user declined to create this user. Do not retry unless they ask you to.');
    }

    try {
      const response = await firstValueFrom(this.userManagementService.createUser(payload));
      this.toastService.success('User created successfully', 'An invitation email has been sent.', 6000);
      return textResult({
        success: true,
        message: asTrimmedString(response?.message) || 'User created. An invitation email has been sent.',
        user: response?.data ?? { email, role },
      });
    } catch (error) {
      return errorResult(`User was not created: ${extractErrorMessage(error)}`);
    }
  }
}
